import { useState, useCallback } from 'react'
import {
  User,
  listUsers,
  createUser,
  deleteUser,
  resetUserPassword,
  CreateUserRequest,
} from '../services/adminApi'

interface UseAdminReturn {
  users: User[]
  isLoading: boolean
  error: string | null
  refreshUsers: () => Promise<void>
  addUser: (data: CreateUserRequest) => Promise<void>
  removeUser: (username: string) => Promise<{ deletedFilesCount: number }>
  resetPassword: (username: string) => Promise<void>
}

export function useAdmin(): UseAdminReturn {
  const [users, setUsers] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refreshUsers = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const userList = await listUsers()
      setUsers(userList)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users')
    } finally {
      setIsLoading(false)
    }
  }, [])

  const addUser = useCallback(async (data: CreateUserRequest) => {
    setError(null)
    try {
      await createUser(data)
      await refreshUsers()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create user'
      setError(message)
      throw err
    }
  }, [refreshUsers])

  const removeUser = useCallback(async (username: string) => {
    setError(null)
    try {
      const result = await deleteUser(username)
      await refreshUsers()
      return { deletedFilesCount: result.deletedFilesCount }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete user'
      setError(message)
      throw err
    }
  }, [refreshUsers])

  const resetPassword = useCallback(async (username: string) => {
    setError(null)
    try {
      await resetUserPassword(username)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reset password'
      setError(message)
      throw err
    }
  }, [])

  return {
    users,
    isLoading,
    error,
    refreshUsers,
    addUser,
    removeUser,
    resetPassword,
  }
}
